import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { FormField, Select } from '../ui/Form';
import { Calculator, AlertTriangle } from 'lucide-react';

interface GenerateDepreciationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (data: { year: number; month: number; fiscalYearId: string }) => Promise<void>;
  fiscalYearId: string;
}

const MONTHS = [
  { value: '1', label: 'Janvier' }, { value: '2', label: 'Février' },
  { value: '3', label: 'Mars' }, { value: '4', label: 'Avril' },
  { value: '5', label: 'Mai' }, { value: '6', label: 'Juin' },
  { value: '7', label: 'Juillet' }, { value: '8', label: 'Août' },
  { value: '9', label: 'Septembre' }, { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' }, { value: '12', label: 'Décembre' },
];

export function GenerateDepreciationModal({ isOpen, onClose, onGenerate, fiscalYearId }: GenerateDepreciationModalProps) {
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [loading, setLoading] = useState(false);

  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const y = now.getFullYear() - 2 + i;
    return { value: String(y), label: String(y) };
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onGenerate({
        year: parseInt(year),
        month: parseInt(month),
        fiscalYearId,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Générer les amortissements" size="md">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <AlertTriangle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-700 dark:text-blue-400">
            <p>
              Cette action va créer les écritures de dotation aux amortissements pour toutes les
              immobilisations actives de la période sélectionnée. Les écritures seront créées
              en <span className="font-semibold">brouillon</span>.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField label="Année" required>
            <Select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              options={yearOptions}
            />
          </FormField>
          <FormField label="Mois" required>
            <Select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              options={MONTHS}
            />
          </FormField>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" icon={Calculator} isLoading={loading}>
            Générer
          </Button>
        </div>
      </form>
    </Modal>
  );
}
